import { useLayoutEffect, useRef, type ElementType, type ReactNode } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useReducedMotion } from '../lib/useReducedMotion.js';

gsap.registerPlugin(ScrollTrigger);

/**
 * Scene — a section that arrives like a shot in a film. As it scrolls into
 * frame, its pieces lift, sharpen (de-blur), and brighten in a staggered
 * sequence rather than all at once. Mark the pieces with `data-cinema`; if none
 * are marked, the Scene's direct children are used.
 *
 * Under prefers-reduced-motion the content is simply present — no hiding, no
 * motion — so it's never gated on animation.
 */
export function Scene({
  children,
  className,
  as,
  y = 46,
  stagger = 0.09,
  start = 'top 82%',
}: {
  children: ReactNode;
  className?: string;
  as?: ElementType;
  /** Initial downward offset (px). */
  y?: number;
  /** Seconds between each piece. */
  stagger?: number;
  /** ScrollTrigger start. */
  start?: string;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const reduced = useReducedMotion();

  useLayoutEffect(() => {
    const el = ref.current;
    if (reduced || !el) return;
    const ctx = gsap.context(() => {
      const marked = el.querySelectorAll<HTMLElement>('[data-cinema]');
      const targets: ArrayLike<Element> = marked.length ? marked : el.children;
      if (!targets.length) return;
      gsap.set(targets, { autoAlpha: 0, y, filter: 'blur(12px)' });
      gsap.to(targets, {
        autoAlpha: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 1.05,
        ease: 'power3.out',
        stagger,
        scrollTrigger: { trigger: el, start, once: true },
      });
    }, el);
    return () => ctx.revert();
  }, [reduced, y, stagger, start]);

  const Tag = (as ?? 'div');
  return (
    <Tag ref={ref as never} className={className}>
      {children}
    </Tag>
  );
}

/**
 * Parallax — drifts an element against the scroll so foreground and background
 * layers separate with depth. `speed` is the fraction of its own height it
 * travels across the full scroll-through (negative = rises faster).
 */
export function Parallax({
  children,
  className,
  as,
  speed = 0.18,
}: {
  children: ReactNode;
  className?: string;
  as?: ElementType;
  speed?: number;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const reduced = useReducedMotion();

  useLayoutEffect(() => {
    const el = ref.current;
    if (reduced || !el) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        el,
        { yPercent: speed * 100 },
        {
          yPercent: -speed * 100,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: 0.6 },
        },
      );
    }, el);
    return () => ctx.revert();
  }, [reduced, speed]);

  const Tag = (as ?? 'div');
  return (
    <Tag ref={ref as never} className={className}>
      {children}
    </Tag>
  );
}
